import { ReactNode, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'

import Header from './Header'
import Footer from './Footer'
import Sidebar from './Sidebar'
import ProgressBar from './ui/ProgressBar'
import ParticlesBackground from './ui/ParticlesBackground'
import BackToTop from './ui/BackToTop'
// import ThemeDebug from './ui/ThemeDebug'
import { useAppStore } from '@store'

interface LayoutProps {
  children: ReactNode
}

const Layout = ({ children }: LayoutProps) => {
  const location = useLocation()
  const { ui } = useAppStore()

  // Handle scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const winScroll = document.body.scrollTop || document.documentElement.scrollTop
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight
      const scrolled = (winScroll / height) * 100
      ui.setScrollProgress(scrolled)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [ui.setScrollProgress])

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  return (
    <div 
      className="min-h-screen relative overflow-x-hidden transition-all duration-300"
      style={{
        backgroundColor: 'var(--bg-primary)',
        color: 'var(--text-primary)'
      }}
    >
      {/* Particles Background */}
      <ParticlesBackground />
      
      {/* Progress Bar */}
      <ProgressBar />
      
      {/* Header */}
      <Header />
      
      {/* Sidebar */}
      <AnimatePresence>
        {ui.sidebarOpen && <Sidebar />}
      </AnimatePresence>
      
      {/* Main Content */}
      <main className="relative z-10">
        <AnimatePresence>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>
      
      {/* Footer */}
      <Footer />
      
      {/* Back to Top Button */}
      <BackToTop />
      
      {/* Theme Debug (temporary) - COMMENTED OUT */}
      {/* <ThemeDebug /> */}
    </div>
  )
}

export default Layout